import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ChevronRight } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { getReadyArticles } from "../data/articles";

// Image mapping for articles
const imageMap = {
  fullBoomWithPanel: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/mqrx914x_HWL_field_still_03_048s.jpg",
  boomLiftElevated: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/3ack3jic_HWL_field_still_04_060s.jpg",
  scissorLiftPair: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/2hzy6y9m_Highwall%20Scissor%20Lift%20Hanger%20Pair.jpg",
  boomLiftHangers2: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/htgmy3k7_Hanger4.jpg",
  boomLiftMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/ujlxoadu_Boom%20Lift%20Hang.jpg",
  liftAndTower: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/9h3z5yos_06_lift-and-tower_square.jpg.jpeg",
  againstBuilding: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/0o94ix6q_07_against-building_square.jpg.jpeg",
  finalPlacement: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/cs2x21mp_08_final-placement_square.jpg.jpeg",
};

const BlogIndex = () => {
  const articles = getReadyArticles();
  
  return (
    <>
      <SEOHead
        title="Blog - Industry Insights & Construction Tips"
        description="Expert insights on panel installation, drywall hanging, IMP installation, and construction equipment. Learn from the Highwall Lift team."
        keywords="construction blog, panel installation tips, drywall installation guide, IMP installation, boom lift tips, scissor lift guide"
        canonicalPath="/blog"
      />
      
      <div className="min-h-screen bg-[#0A0A0A] pt-20">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-white">Blog</span>
          </nav>
        </div>
        
        {/* Header */}
        <section className="py-12 sm:py-16 bg-[#141414]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-sm font-bold mb-4">
              Industry Insights
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase text-white mb-6">
              Highwall Lift Blog
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl">
              Expert insights on panel installation, construction equipment, and best practices 
              for commercial drywall and IMP installation crews.
            </p>
          </div>
        </section>
        
        {/* Articles Grid */}
        <section className="py-16 sm:py-24" data-testid="blog-index">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            {/* Featured Article */}
            {articles.length > 0 && (
              <article className="mb-16" data-testid="featured-article">
                <Link to={`/blog/${articles[0].slug}`} className="group block">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-[#1A1A1A] border border-white/10 overflow-hidden">
                    <div className="aspect-video lg:aspect-auto overflow-hidden">
                      <img 
                        src={imageMap[articles[0].image]}
                        alt={articles[0].imageAlt || articles[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-3">
                        Featured Article
                      </p>
                      <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {articles[0].date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {articles[0].readTime}
                        </span>
                      </div>
                      <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight group-hover:text-signal-red transition-colors">
                        {articles[0].title}
                      </h2>
                      <p className="text-gray-400 leading-relaxed mb-6">
                        {articles[0].excerpt}
                      </p>
                      <span className="inline-flex items-center gap-2 text-signal-red font-bold uppercase text-sm tracking-wide group-hover:gap-4 transition-all">
                        Read Article
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            )}
            
            {/* Article Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.slice(1).map((article) => (
                <article 
                  key={article.id}
                  className="bg-[#1A1A1A] border border-white/10 overflow-hidden group"
                  data-testid={`blog-card-${article.slug}`}
                >
                  <Link to={`/blog/${article.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={imageMap[article.image]}
                        alt={article.imageAlt || article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-gray-500 text-xs mb-3">
                        <span className="bg-signal-red/10 text-signal-red px-2 py-1 font-mono uppercase tracking-wide">
                          {article.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg font-bold text-white mb-3 leading-tight group-hover:text-signal-red transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
            
            {articles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">No articles available yet. Check back soon!</p>
              </div>
            )}
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-16 bg-signal-red">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase text-white mb-6">
              Ready to Improve Your Panel Installation Workflow?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              See how Highwall Lift can help your crew install panels faster and more safely.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/#quote"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black font-bold uppercase tracking-wide hover:bg-white/90 transition-all duration-300"
                data-testid="blog-cta-quote"
              >
                Request a Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/#products"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-bold uppercase tracking-wide border-2 border-white hover:bg-white/10 transition-all duration-300"
                data-testid="blog-cta-products"
              >
                View Products
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogIndex;
